import React from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Badge } from '../ui/badge';
import { Monitor, Smartphone, LayoutList, Users, Mail, BarChart3, FileBarChart, Lock, Home, CheckCircle2, FileText, Sparkles } from 'lucide-react';

/**
 * Top navigation bar for the WFH Survey prototype.
 *
 * Lets reviewers jump between every screen state in the prototype.
 * Grouped by Admin (desktop) vs Employee (mobile-first) plus Dialog-only states.
 */

const ADMIN_LINKS = [
  { to: '/prototype/wfh-survey/admin/list', label: 'Campaigns list', icon: LayoutList },
  { to: '/prototype/wfh-survey/admin/list?state=empty', label: 'Campaigns (empty)', icon: LayoutList, query: 'state=empty' },
  { to: '/prototype/wfh-survey/admin/new', label: 'Create campaign', icon: Sparkles },
  { to: '/prototype/wfh-survey/admin/campaign/camp-draft?tab=employees', label: 'Draft: employees', icon: Users, query: 'tab=employees' },
  { to: '/prototype/wfh-survey/admin/campaign/camp-2025-wfh?tab=dashboard', label: 'Active: dashboard', icon: BarChart3, query: 'tab=dashboard' },
  { to: '/prototype/wfh-survey/admin/campaign/camp-2025-wfh?tab=employees', label: 'Active: invitations', icon: Mail, query: 'tab=employees' },
  { to: '/prototype/wfh-survey/admin/campaign/camp-closing?tab=dashboard', label: 'Calculating', icon: Lock, query: 'tab=dashboard' },
  { to: '/prototype/wfh-survey/admin/campaign/camp-2024-wfh?tab=results', label: 'Closed: results', icon: FileBarChart, query: 'tab=results' },
  { to: '/prototype/wfh-survey/admin/campaign/camp-2024-wfh?tab=consultant', label: 'Consultant audit', icon: FileText, query: 'tab=consultant' },
];

const EMPLOYEE_LINKS = [
  { to: '/prototype/wfh-survey/employee/welcome', label: 'Welcome', icon: Home },
  { to: '/prototype/wfh-survey/employee/step/employment', label: 'About you', icon: Smartphone },
  { to: '/prototype/wfh-survey/employee/step/working-pattern', label: 'Working pattern', icon: Smartphone },
  { to: '/prototype/wfh-survey/employee/step/wfh-equipment', label: 'Equipment', icon: Smartphone },
  { to: '/prototype/wfh-survey/employee/step/lighting', label: 'Lighting', icon: Smartphone },
  { to: '/prototype/wfh-survey/employee/step/heating', label: 'Heating', icon: Smartphone },
  { to: '/prototype/wfh-survey/employee/step/cooling', label: 'Cooling', icon: Smartphone },
  { to: '/prototype/wfh-survey/employee/step/renewable', label: 'Renewable energy', icon: Smartphone },
  { to: '/prototype/wfh-survey/employee/step/commuting', label: 'Commuting', icon: Smartphone },
  { to: '/prototype/wfh-survey/employee/step/review', label: 'Review & submit', icon: Smartphone },
  { to: '/prototype/wfh-survey/employee/thank-you', label: 'Thank you', icon: CheckCircle2 },
];

const NavGroup = ({ title, links, icon: GroupIcon }) => {
  const { pathname, search } = useLocation();
  const current = `${pathname}${search}`;
  return (
    <div>
      <div className='flex items-center gap-2 px-3 py-2 font-mono text-[10px] tracking-[0.18em] uppercase text-muted-foreground'>
        {GroupIcon && <GroupIcon size={12} strokeWidth={1.5} />}
        {title}
      </div>
      <div className='flex flex-col gap-0.5'>
        {links.map((link) => {
          const Icon = link.icon;
          const isActive =
            current === link.to ||
            (link.query ? current.includes(link.query) && pathname.startsWith(link.to.split('?')[0]) : pathname === link.to);
          return (
            <NavLink
              key={link.to}
              to={link.to}
              className={`flex items-center gap-2 px-3 py-1.5 text-[13px] rounded-md transition-colors ${
                isActive ? 'bg-primary/10 text-accent font-semibold' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}>
              <Icon size={14} strokeWidth={1.5} />
              <span className='truncate'>{link.label}</span>
            </NavLink>
          );
        })}
      </div>
    </div>
  );
};

const PrototypeNav = () => {
  return (
    <aside
      className='hidden lg:flex flex-col fixed top-0 left-0 h-screen w-[260px] border-r border-border bg-card overflow-y-auto z-40'
      aria-label='Prototype navigator'>
      <Link
        to='/prototype/wfh-survey/admin/list'
        className='px-4 py-5 border-b border-border flex items-center gap-2.5 hover:bg-background/50 transition-colors'>
        <div className='w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center text-accent'>
          <BarChart3 size={18} strokeWidth={1.5} />
        </div>
        <div>
          <div className='font-display text-[15px] leading-tight'>WFH Survey</div>
          <div className='text-[11px] text-muted-foreground'>Prototype preview</div>
        </div>
      </Link>

      <div className='px-3 py-4 flex flex-col gap-5 flex-1'>
        <NavGroup title='Admin surface' icon={Monitor} links={ADMIN_LINKS} />
        <NavGroup title='Employee surface' icon={Smartphone} links={EMPLOYEE_LINKS} />
      </div>

      <div className='px-4 py-3 border-t border-border text-[11px] text-muted-foreground'>
        <Badge variant='warning' size='sm'>
          Prototype
        </Badge>
        <span className='ml-2'>No real data. Stubbed UI only.</span>
      </div>
    </aside>
  );
};

export default PrototypeNav;
